import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandMark, EventismCredit } from "@/components/Brand";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await auth.login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate(u.role === "admin" ? "/admin" : next);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-slate flex flex-col">
      <div className="px-6 py-5 flex justify-between items-center border-b border-slate-100 bg-white">
        <BrandMark />
        <Link
          to="/"
          className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-brand-red"
          data-testid="login-back-home-link"
        >
          <i className="fas fa-arrow-left mr-2" /> Back
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-[32px] shadow-xl border border-slate-100 overflow-hidden">
          <div className="bg-brand-blue text-white px-8 py-7">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-2">
              <i className="fas fa-bolt mr-2" /> Pod Access
            </p>
            <h1 className="text-3xl font-black uppercase tracking-tightest">Welcome Back</h1>
            <p className="text-white/70 text-xs font-bold mt-1">
              Manage rentals and your pod PIN.
            </p>
          </div>
          <form onSubmit={submit} className="px-8 py-7 space-y-4" data-testid="login-form">
            <Field
              id="login-email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              testId="login-email-input"
              required
            />
            <Field
              id="login-password"
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              testId="login-password-input"
              required
            />
            {error && (
              <div
                className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-brand-red"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Signing in</> : <>Log In</>}
            </button>
            <p className="text-center text-xs font-bold text-slate-500 mt-2">
              No account?{" "}
              <Link to="/register" className="text-brand-red underline" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </form>
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center">
            <EventismCredit />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, type, value, onChange, required, testId }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1.5"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
        data-testid={testId}
      />
    </div>
  );
}
